import React from 'react';
import { Button, Spinner, Alert } from '@parsina/framework';
import { Coins, LogIn } from 'lucide-react';
import logo from 'url:../../assets/logo-white.png';

import { C, CT, GRADIENT_BRAND_DARK, soft } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { LOGIN_FEATURES } from './Login.shared.js';

// ورود — the phone sign-in screen.
//
// The desktop's two columns become one, and the sign-in control DOCKS to the
// bottom edge above the safe-area inset rather than sitting after the feature
// list: on a 360×640 screen the list is what pushes it below the fold, and the
// one thing this screen is for must never need a scroll to reach.
//
// It is the same three sentences and the same single redirect. Nothing about the
// auth flow forks by surface.

export default function LoginMobile({ onSignIn, busy = false, notice = '' }) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: GRADIENT_BRAND_DARK,
        color: '#fff',
        paddingInlineStart: 'env(safe-area-inset-left, 0px)',
        paddingInlineEnd: 'env(safe-area-inset-right, 0px)',
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: 'calc(1.75rem + env(safe-area-inset-top, 0px)) 1.25rem 1.25rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <img src={logo} alt="" style={{ height: '2.1rem', width: 'auto', objectFit: 'contain' }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{t('nav.brand.name')}</div>
            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)' }}>
              {t('nav.brand.panel')}
            </div>
          </div>
        </div>

        <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, lineHeight: 1.5 }}>
          {t('login.title')}
        </h1>
        <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.9, color: 'rgba(255,255,255,0.72)' }}>
          {t('login.sub')}
        </p>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            background: soft(C.teal, 0.2),
            color: '#fff',
            borderRadius: 999,
            padding: '6px 14px',
            fontSize: 12.5,
            fontWeight: 700,
            marginBottom: 20,
          }}
        >
          <Coins size={14} aria-hidden />
          {t('login.free')}
        </div>

        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {LOGIN_FEATURES.map(({ id, titleKey, bodyKey, Icon }) => (
            <li key={id} style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
              <span
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.12)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon size={16} aria-hidden />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{t(titleKey)}</div>
                <div style={{ fontSize: 12.5, lineHeight: 1.8, color: 'rgba(255,255,255,0.6)' }}>
                  {t(bodyKey)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div
        style={{
          flexShrink: 0,
          background: C.card,
          borderStartStartRadius: 20,
          borderStartEndRadius: 20,
          padding: '1.25rem 1.25rem calc(1.25rem + env(safe-area-inset-bottom, 0px))',
          textAlign: 'center',
        }}
      >
        {/* `invalid_redirect_uri` — this origin is not registered on the
            `parsina-student` client. Retrying cannot fix it, so the sentence
            is the answer, not the button (core.md §7.1). */}
        {notice && (
          <Alert variant="warning" style={{ marginBottom: 12, textAlign: 'start' }} data-testid="login-notice">
            {notice}
          </Alert>
        )}
        <Button
          size="lg"
          onClick={onSignIn}
          disabled={busy}
          className="w-full min-h-12"
          data-testid="login-action"
        >
          {busy ? <Spinner size={16} color="#fff" /> : <LogIn size={17} aria-hidden />}
          {busy ? t('login.working') : t('login.action')}
        </Button>
        <p style={{ margin: '12px 0 0', fontSize: 12, lineHeight: 1.9, color: CT.mute }}>
          {t('login.hint')}
        </p>
      </div>
    </div>
  );
}
